import React, { useState, useEffect } from 'react';
import { 
  TestTube, 
  CheckCircle, 
  AlertCircle, 
  TrendingUp, 
  Filter,
  Star,
  Users,
  Target,
  BarChart3,
  X,
  Play,
  RefreshCw
} from 'lucide-react';
import { Business, User, UserInterest } from '../types';
import FilteringEngine from '../utils/filteringEngine';

interface FilteringTestPanelProps {
  isOpen: boolean;
  onClose: () => void;
  businesses: Business[];
  user: User;
}

interface TestResult {
  testName: string;
  passed: boolean;
  details: string;
  score?: number;
  expectedCount?: number;
  actualCount?: number;
}

interface TestScenario {
  name: string;
  interests: UserInterest[];
  expectedBusinessIds: string[];
  description: string;
}

const FilteringTestPanel: React.FC<FilteringTestPanelProps> = ({ 
  isOpen, 
  onClose, 
  businesses, 
  user 
}) => {
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [isRunningTests, setIsRunningTests] = useState(false);
  const [overallScore, setOverallScore] = useState(0);

  if (!isOpen) return null;

  const testScenarios: TestScenario[] = [
    {
      name: 'Food & Dining Interest',
      interests: [
        { id: 'restaurants', name: 'Restaurants', category: 'food_dining', weight: 5 }
      ],
      expectedBusinessIds: ['1', '2'], // Coffee shop and bistro
      description: 'Should prioritize food-related businesses'
    },
    {
      name: 'Technology Interest',
      interests: [
        { id: 'tech-services', name: 'Tech Services', category: 'technology', weight: 4 }
      ],
      expectedBusinessIds: ['3'], // Tech Repair Pro
      description: 'Should show tech-related businesses'
    },
    {
      name: 'Health & Wellness Interest',
      interests: [
        { id: 'fitness-gyms', name: 'Fitness & Gyms', category: 'health_wellness', weight: 5 }
      ],
      expectedBusinessIds: ['2', '4'], // Bistro (healthy food) and Yoga Studio
      description: 'Should show health and wellness businesses'
    },
    {
      name: 'Multiple Interests',
      interests: [
        { id: 'restaurants', name: 'Restaurants', category: 'food_dining', weight: 4 },
        { id: 'entertainment', name: 'Entertainment', category: 'entertainment', weight: 3 }
      ],
      expectedBusinessIds: ['1', '2', '6'], // Food places and Jazz Club
      description: 'Should balance multiple interest categories'
    },
    {
      name: 'High Weight Preference',
      interests: [
        { id: 'coffee', name: 'Coffee Shops', category: 'food_dining', weight: 5 },
        { id: 'tech', name: 'Technology', category: 'technology', weight: 1 }
      ],
      expectedBusinessIds: ['1'], // Should heavily favor coffee shop
      description: 'Should prioritize higher-weighted interests'
    }
  ];

  const runFilteringTests = async () => {
    setIsRunningTests(true);
    const results: TestResult[] = [];
    let totalScore = 0;

    // Test 1: Interest Matching Accuracy
    for (const scenario of testScenarios) {
      await new Promise(resolve => setTimeout(resolve, 200)); // Simulate processing time

      const filteredBusinesses = FilteringEngine.filterBusinesses(businesses, {
        userInterests: scenario.interests,
        minimumRating: 3.0
      });

      const topResults = filteredBusinesses.slice(0, scenario.expectedBusinessIds.length);
      const actualIds = topResults.map(b => b.id);
      const matchCount = scenario.expectedBusinessIds.filter(id => actualIds.includes(id)).length;
      const accuracy = matchCount / scenario.expectedBusinessIds.length;
      
      results.push({
        testName: scenario.name,
        passed: accuracy >= 0.5, // At least 50% of expected businesses should be in results
        details: `Expected ${scenario.expectedBusinessIds.length} relevant businesses, found ${matchCount} matches. ${scenario.description}`,
        score: accuracy * 100,
        expectedCount: scenario.expectedBusinessIds.length,
        actualCount: matchCount
      });

      totalScore += accuracy * 100;
    }

    // Test 2: Relevance Score Calculation
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const testInterests: UserInterest[] = [
      { id: 'coffee', name: 'Coffee', category: 'food_dining', weight: 5 }
    ];
    
    const coffeeShop = businesses.find(b => b.id === '1'); // Artisan Coffee Roasters
    const techShop = businesses.find(b => b.id === '3'); // Tech Repair Pro
    
    if (coffeeShop && techShop) {
      const coffeeScore = FilteringEngine.calculateBusinessRelevance(coffeeShop, testInterests);
      const techScore = FilteringEngine.calculateBusinessRelevance(techShop, testInterests);
      
      const scoreTestPassed = coffeeScore > techScore;
      results.push({
        testName: 'Relevance Score Calculation',
        passed: scoreTestPassed,
        details: `Coffee shop scored ${coffeeScore.toFixed(1)}, tech shop scored ${techScore.toFixed(1)}. Coffee shop should score higher for coffee interest.`,
        score: scoreTestPassed ? 100 : 0
      });
      
      totalScore += scoreTestPassed ? 100 : 0;
    }

    // Test 3: Distance Filtering
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const distanceFiltered = FilteringEngine.filterBusinesses(businesses, {
      userInterests: [{ id: 'any', name: 'Any', category: 'food_dining', weight: 3 }],
      maxDistance: 1, // Very small distance
      userLocation: { lat: 40.7128, lng: -74.0060 } // NYC center
    });
    
    const distanceTestPassed = distanceFiltered.length <= businesses.length;
    results.push({
      testName: 'Distance Filtering',
      passed: distanceTestPassed,
      details: `Distance filter reduced results from ${businesses.length} to ${distanceFiltered.length} businesses within 1 mile.`,
      score: distanceTestPassed ? 100 : 0,
      expectedCount: businesses.length,
      actualCount: distanceFiltered.length
    });
    
    totalScore += distanceTestPassed ? 100 : 0;

    // Test 4: Rating Filtering
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const highRatingFiltered = FilteringEngine.filterBusinesses(businesses, {
      userInterests: [{ id: 'any', name: 'Any', category: 'food_dining', weight: 3 }],
      minimumRating: 4.8
    });
    
    const allHighRated = highRatingFiltered.every(b => (b.averageRating || 0) >= 4.8);
    results.push({
      testName: 'Rating Filtering',
      passed: allHighRated,
      details: `All ${highRatingFiltered.length} filtered businesses have rating >= 4.8. Highest: ${Math.max(...highRatingFiltered.map(b => b.averageRating || 0)).toFixed(1)}`,
      score: allHighRated ? 100 : 0,
      expectedCount: businesses.filter(b => (b.averageRating || 0) >= 4.8).length,
      actualCount: highRatingFiltered.length
    });
    
    totalScore += allHighRated ? 100 : 0;

    // Test 5: User Interest Analysis
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const analysis = FilteringEngine.analyzeUserInterests(user.interests || []);
    const analysisTestPassed = analysis.topCategories.length > 0 || (user.interests || []).length === 0;
    
    results.push({
      testName: 'Interest Analysis',
      passed: analysisTestPassed,
      details: `Analyzed ${(user.interests || []).length} user interests. Top categories: ${analysis.topCategories.join(', ') || 'None'}. Diversity score: ${(analysis.diversityScore * 100).toFixed(1)}%`,
      score: analysisTestPassed ? 100 : 0
    });
    
    totalScore += analysisTestPassed ? 100 : 0;

    const averageScore = results.length > 0 ? totalScore / results.length : 0;
    setOverallScore(averageScore);
    setTestResults(results);
    setIsRunningTests(false);
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBg = (score: number) => {
    if (score >= 90) return 'bg-green-100';
    if (score >= 70) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <TestTube className="w-6 h-6 text-primary-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Filtering System Validation</h2>
              <p className="text-sm text-gray-600">Test personalization accuracy and performance</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {/* Test Controls */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-medium text-gray-900">Automated Test Suite</h3>
              <p className="text-sm text-gray-600 mt-1">
                Validates filtering logic, relevance scoring, and personalization accuracy
              </p>
            </div>
            <button
              onClick={runFilteringTests}
              disabled={isRunningTests}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isRunningTests ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Running Tests...</span>
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  <span>Run Validation Tests</span>
                </>
              )}
            </button>
          </div>

          {/* Overall Score */}
          {testResults.length > 0 && (
            <div className={`${getScoreBg(overallScore)} border border-current border-opacity-20 rounded-lg p-4 mb-6`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <BarChart3 className={`w-6 h-6 ${getScoreColor(overallScore)}`} />
                  <div>
                    <h4 className={`font-semibold ${getScoreColor(overallScore)}`}>
                      Overall Test Score: {overallScore.toFixed(1)}%
                    </h4>
                    <p className="text-sm text-gray-600">
                      {testResults.filter(r => r.passed).length} of {testResults.length} tests passed
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-2xl font-bold ${getScoreColor(overallScore)}`}>
                    {overallScore >= 90 ? 'Excellent' : overallScore >= 70 ? 'Good' : 'Needs Work'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Test Results */}
          {testResults.length > 0 && (
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900">Test Results</h4>
              {testResults.map((result, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      {result.passed ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-600" />
                      )}
                      <h5 className="font-medium text-gray-900">{result.testName}</h5>
                    </div>
                    {result.score !== undefined && (
                      <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                        result.passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {result.score.toFixed(1)}%
                      </div>
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-700 mb-2">{result.details}</p>
                  
                  {result.expectedCount !== undefined && result.actualCount !== undefined && (
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span>Expected: {result.expectedCount}</span>
                      <span>Actual: {result.actualCount}</span>
                      <span>
                        Accuracy: {result.expectedCount > 0 ? 
                          ((result.actualCount / result.expectedCount) * 100).toFixed(1) : '100'}%
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Test Information */}
          {testResults.length === 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <Target className="w-6 h-6 text-blue-600 mt-1" />
                <div>
                  <h4 className="font-medium text-blue-900 mb-2">What We Test</h4>
                  <ul className="text-sm text-blue-800 space-y-2">
                    <li><strong>Interest Matching:</strong> Verifies businesses match user interests</li>
                    <li><strong>Relevance Scoring:</strong> Ensures higher scores for more relevant businesses</li>
                    <li><strong>Distance Filtering:</strong> Tests location-based filtering accuracy</li>
                    <li><strong>Rating Filtering:</strong> Validates minimum rating requirements</li>
                    <li><strong>Interest Analysis:</strong> Checks user preference analysis accuracy</li>
                  </ul>
                  
                  <div className="mt-4 p-3 bg-blue-100 rounded-lg">
                    <h5 className="font-medium text-blue-900 mb-1">Current User Profile</h5>
                    <p className="text-sm text-blue-800">
                      {user.interests && user.interests.length > 0 
                        ? `${user.interests.length} interests selected: ${user.interests.slice(0, 3).map(i => i.name).join(', ')}${user.interests.length > 3 ? '...' : ''}`
                        : 'No interests selected - tests will use sample data'
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilteringTestPanel;



